// Grace period before stopping a call when the page is hidden (tab switch, minimise).
// Short enough to catch "walked away" but long enough not to interrupt a brief tab switch.
const VISIBILITY_GRACE_MS = 5 * 60 * 1000; // 5 minutes

let stopOnUnloadHandler: ((e: Event) => void) | null = null;
let visibilityStopHandler: (() => void) | null = null;
let visibilityGraceTimer: ReturnType<typeof setTimeout> | null = null;

/** Fire-and-forget stop request — safe to call from both beforeunload and the visibility timer. */
function fireSyncStop(get: any): void {
  const { sdk, salesCallStatus } = get();
  if (!sdk || !salesCallStatus?.isActive) return;

  const stopPath = salesCallStatus.callMode === 'simulator'
    ? '/sales/stop-simulator-call'
    : '/sales/stop-sale-call';
  const url = `${sdk._cfg.baseURL}${stopPath}`;

  try {
    if (typeof navigator !== 'undefined' && 'sendBeacon' in navigator) {
      navigator.sendBeacon(url, new Blob([''], { type: 'application/json' }));
    } else {
      // @ts-ignore keepalive is supported in modern browsers
      fetch(url, {
        method: 'POST',
        credentials: 'include',
        // @ts-ignore
        keepalive: true,
        headers: { 'Content-Type': 'application/json' },
        body: '',
      }).catch(() => {});
    }
  } catch {
    // ignore
  }
}

// ── beforeunload (tab / browser close, page refresh) ──────────────────────────

export const registerStopOnUnload = (get: any) => {
  if (typeof window === 'undefined' || stopOnUnloadHandler) return;

  stopOnUnloadHandler = () => fireSyncStop(get);
  window.addEventListener('beforeunload', stopOnUnloadHandler);
};

export const unregisterStopOnUnload = () => {
  if (typeof window === 'undefined' || !stopOnUnloadHandler) return;
  window.removeEventListener('beforeunload', stopOnUnloadHandler);
  stopOnUnloadHandler = null;
};

// ── visibilitychange (tab switch, window minimise, screen lock) ────────────────
//
// When the page goes hidden we start a 5-minute grace timer.  If the user
// comes back before the timer fires we cancel it.  If they don't, we send a
// stop-beacon so the audioServer's summarizer ticker stops burning tokens.

export const registerVisibilityStop = (get: any) => {
  if (typeof document === 'undefined' || visibilityStopHandler) return;

  visibilityStopHandler = () => {
    if (document.visibilityState === 'hidden') {
      if (visibilityGraceTimer) return; // already counting down
      visibilityGraceTimer = setTimeout(() => {
        visibilityGraceTimer = null;
        if (get().salesCallStatus?.isActive) {
          console.info('[SALES] Auto-stopping call: page hidden for 5 minutes with no return');
          fireSyncStop(get);
        }
      }, VISIBILITY_GRACE_MS);
    } else {
      // User came back — cancel the pending stop
      if (visibilityGraceTimer) {
        clearTimeout(visibilityGraceTimer);
        visibilityGraceTimer = null;
      }
    }
  };

  document.addEventListener('visibilitychange', visibilityStopHandler);
};

export const unregisterVisibilityStop = () => {
  if (typeof document === 'undefined') return;
  if (visibilityGraceTimer) {
    clearTimeout(visibilityGraceTimer);
    visibilityGraceTimer = null;
  }
  if (visibilityStopHandler) {
    document.removeEventListener('visibilitychange', visibilityStopHandler);
    visibilityStopHandler = null;
  }
};
